import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { WordCard } from '@/components/WordCard';
import { StarBurst } from '@/components/HeroReward';
import { useStorage } from '@/hooks/useStorage';
import { speakWord, POINTS_CONFIG, type WordEntry } from '@/lib/index';
import { PINYIN_INITIALS, PINYIN_FINALS, HIRAGANA_ROWS, KATAKANA_ROWS, ENGLISH_LETTERS } from '@/data/phonicsData';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Volume2, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Learn() {
  const { storage, getTodayWords, unlockTodaySession, updateWordProgress, addPoints } = useStorage();
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('words');
  const [showCelebration, setShowCelebration] = useState(false);
  const [todayWords, setTodayWords] = useState<WordEntry[]>([]);

  const isUnlocked = storage.todayUnlocked;

  useEffect(() => {
    if (isUnlocked) {
      const words = getTodayWords();
      setTodayWords(words);
    }
  }, [isUnlocked, getTodayWords]);

  const handleUnlock = () => {
    unlockTodaySession();
  };

  const handleWordMastery = async (lang: 'zh' | 'jp' | 'en', level: 0 | 1 | 2) => {
    const word = todayWords[currentWordIndex];
    if (!word) return;
    
    await updateWordProgress(word.id, lang, level);
    
    const points = level === 2 ? POINTS_CONFIG.mastery : level === 1 ? POINTS_CONFIG.practice : POINTS_CONFIG.cheer;
    if (points > 0) {
      await addPoints(points, `${lang}语言掌握度更新`);
    }
  };

  const handlePrevWord = () => {
    if (currentWordIndex > 0) {
      setCurrentWordIndex(currentWordIndex - 1);
    }
  };

  const handleNextWord = () => {
    if (currentWordIndex < todayWords.length - 1) {
      setCurrentWordIndex(currentWordIndex + 1);
    } else {
      setShowCelebration(true);
      toast.success('今日单词学习完成！');
    }
  };

  const getDayIndex = () => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 0);
    const diff = today.getTime() - startOfYear.getTime();
    const oneDay = 1000 * 60 * 60 * 24;
    return Math.floor(diff / oneDay);
  };

  const getTodayPhonics = () => {
    const dayIndex = getDayIndex();
    const dailyPhonicsCount = storage.dailyPhonicsCount || 10;
    const items: Array<{
      type: 'pinyin' | 'hiragana' | 'katakana' | 'letter';
      data: any;
    }> = [];

    const pinyinItem = PINYIN_INITIALS[dayIndex % PINYIN_INITIALS.length];
    items.push({ type: 'pinyin', data: pinyinItem });

    const hiraganaRowIndex = dayIndex % HIRAGANA_ROWS.length;
    const hiraganaRow = HIRAGANA_ROWS[hiraganaRowIndex];
    const katakanaRow = KATAKANA_ROWS[hiraganaRowIndex];
    
    hiraganaRow.kanas.forEach((hira, idx) => {
      const kata = katakanaRow.kanas[idx];
      items.push({
        type: 'hiragana',
        data: { hiragana: hira, katakana: kata }
      });
    });

    const letterItem = ENGLISH_LETTERS[dayIndex % ENGLISH_LETTERS.length];
    items.push({ type: 'letter', data: letterItem });

    return items.slice(0, dailyPhonicsCount);
  };

  const phonicsItems = getTodayPhonics();

  if (!isUnlocked) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="max-w-md w-full bg-card border-2 border-border rounded-3xl p-12 text-center shadow-lg"
          >
            <div className="text-8xl mb-6">🐼</div>
            <h2 className="text-3xl font-bold mb-4 text-foreground">今日学习未开始</h2>
            <p className="text-lg text-muted-foreground mb-8">准备好开始今天的三语学习之旅了吗？</p>
            <Button
              size="lg"
              onClick={handleUnlock}
              className="text-2xl px-10 py-8 rounded-3xl bg-primary hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.97]"
            >
              🚀 开始今日学习
            </Button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  if (todayWords.length === 0) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-card border-2 border-border rounded-3xl p-12 text-center shadow-lg">
            <div className="text-8xl mb-6">🎉</div>
            <h2 className="text-3xl font-bold mb-4 text-foreground">今日单词已学完</h2>
            <p className="text-lg text-muted-foreground">明天再来学习新单词吧！</p>
          </div>
        </div>
      </Layout>
    );
  }

  const currentWord = todayWords[currentWordIndex];
  const progress = ((currentWordIndex + 1) / todayWords.length) * 100;

  return (
    <Layout>
      <div className="min-h-screen py-8 px-4 md:px-0">
        <div className="max-w-2xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="words" className="text-lg py-3">📚 单词学习</TabsTrigger>
              <TabsTrigger value="phonics" className="text-lg py-3">🎵 发音练习</TabsTrigger>
            </TabsList>

            <TabsContent value="words" className="space-y-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-3xl font-bold text-foreground">📚 今日学习</h1>
                  <div className="text-2xl font-bold text-primary">
                    {currentWordIndex + 1} / {todayWords.length}
                  </div>
                </div>
                <Progress value={progress} className="h-3" />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentWordIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                  <WordCard 
                    word={currentWord} 
                    showAll={true}
                    onMasteryChange={handleWordMastery}
                  />
                </motion.div>
              </AnimatePresence>

              <div className="flex justify-between items-center gap-4">
                <Button
                  size="lg"
                  onClick={handlePrevWord}
                  disabled={currentWordIndex === 0}
                  variant="outline"
                  className="text-xl px-6 py-6 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.97]"
                >
                  <ChevronLeft className="w-6 h-6 mr-2" />
                  上一个
                </Button>
                <Button
                  size="lg"
                  onClick={handleNextWord}
                  className="text-xl px-6 py-6 rounded-2xl bg-primary hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.97]"
                >
                  下一个
                  <ChevronRight className="w-6 h-6 ml-2" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="phonics" className="space-y-6">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground mb-4">🎵 今日发音练习</h1>
                <p className="text-lg text-muted-foreground">点击每个发音项目听一听</p>
              </div>

              <div className="space-y-4">
                {phonicsItems.map((item, idx) => {
                  if (item.type === 'pinyin') {
                    return (
                      <motion.div
                        key={`pinyin-${idx}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="bg-lang-zh-bg border-2 border-lang-zh-border rounded-3xl p-6 cursor-pointer hover:shadow-lg transition-all"
                        onClick={() => speakWord(item.data.speakChar, 'zh', item.data.speakChar)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-5xl">{item.data.emoji}</div>
                          <div className="flex-1">
                            <div className="text-4xl font-bold text-lang-zh mb-1">{item.data.symbol}</div>
                            <div className="text-sm text-muted-foreground">示例：{item.data.example}</div>
                          </div>
                          <Volume2 className="w-8 h-8 text-lang-zh" />
                        </div>
                      </motion.div>
                    );
                  }

                  if (item.type === 'hiragana') {
                    return (
                      <motion.div
                        key={`kana-${idx}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="bg-lang-jp-bg border-2 border-lang-jp-border rounded-3xl p-6 cursor-pointer hover:shadow-lg transition-all"
                        onClick={() => speakWord(item.data.hiragana.char, 'jp')}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <div className="text-center">
                              <div className="text-4xl font-bold text-lang-jp mb-1">{item.data.hiragana.char}</div>
                              <div className="text-xs text-muted-foreground">平假名</div>
                            </div>
                            <div className="text-2xl text-muted-foreground">·</div>
                            <div className="text-center">
                              <div className="text-4xl font-bold text-lang-jp/70 mb-1">{item.data.katakana.char}</div>
                              <div className="text-xs text-muted-foreground">片假名</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-sm text-muted-foreground">{item.data.hiragana.romaji}</div>
                            <Volume2 className="w-8 h-8 text-lang-jp" />
                          </div>
                        </div>
                      </motion.div>
                    );
                  }

                  if (item.type === 'letter') {
                    return (
                      <motion.div
                        key={`letter-${idx}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="bg-lang-en-bg border-2 border-lang-en-border rounded-3xl p-6 cursor-pointer hover:shadow-lg transition-all"
                        onClick={() => speakWord(item.data.upper, 'en')}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-5xl">{item.data.emoji}</div>
                          <div className="flex-1">
                            <div className="text-4xl font-bold text-lang-en mb-1">
                              {item.data.upper}{item.data.lower}
                            </div>
                            <div className="text-sm text-muted-foreground">{item.data.example}</div>
                          </div>
                          <Volume2 className="w-8 h-8 text-lang-en" />
                        </div>
                      </motion.div>
                    );
                  }

                  return null;
                })}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <StarBurst show={showCelebration} onComplete={() => setShowCelebration(false)} />
    </Layout>
  );
}
